import React from 'react'

import {Colors, S1, S2, P, IconButton, Row, Icon, Popup, Badge} from '../../src/components/UI/index.js'
import _ from 'lodash'
import dbCodes from '../../server/dbCodes.js'
import moment from 'moment'
import OrderActions from '../actions/OrderActions.js'
import {formatDuration} from '@utils/utils.js'
import utils from '../utils/utils.js'
import {emit} from 'messenger/socketio/socketEmit.js'
import {OptionIcons, CustomOptionBadges} from './OptionIcons.js'
import OriginalAddressRow from './OriginalAddressRow.js'

export default class Address extends React.Component {
    constructor(props) {
        super(props)

        this.state = {tooltipTimeout: null}
    }

    componentWillUnmount() {
        clearTimeout(this.state.tooltipTimeout)
    }

    render() {
        const {reseller, route, address, compact, showCollo, onClick, onClickNavigation, onSetColloReady, onClickMap, isSibling, editRoute, disabled, createdByMessenger, editable} = this.props

        const lastHistoryEntry = address.hasArrived && _.find([...address.history].reverse(), (entry) => {
            return entry.type === address.type && entry.addressIndex === address.index
        })

        const colli = lastHistoryEntry?.colli || address.colli

        // LEGACY: remove once no messenger opens a route from before #3671 anymore.
        // A completed address without colli is an old snapshot whose order left the route, its outcome is unknown
        const {status, statusColor} = address.hasArrived && !colli?.length && address.status !== dbCodes.status.geannuleerd() ? {} : Colors.addressStatusColor(address, colli)

        const isReady = (!disabled && address.isReady && (utils.nextStopDeviation(reseller, address) !== 'notallowed' || address.isDepot))

        // A completed address can only be opened when the messenger is allowed to change it (#701)
        const clickable = !showCollo && !disabled && !address.isDepot && (address.hasArrived ? editable : isReady)

        let inTimeSlot = true
        let beforeTimeSlot = false

        if (address.plannedTimeOfArrival) {
            inTimeSlot = moment(address.plannedTimeOfArrival, 'HH:mm').isBetween(moment(address.startTime, 'HH:mm'), moment(address.endTime, 'HH:mm'))

            if (!inTimeSlot) {
                beforeTimeSlot = moment(address.plannedTimeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
            }
        } else if (typeof address.duration === 'number') {
            inTimeSlot = moment().add(address.duration || 0, 'seconds').isBetween(moment(address.startTime, 'HH:mm'), moment(address.endTime, 'HH:mm'))

            if (!inTimeSlot) {
                beforeTimeSlot = moment().add(address.duration || 0, 'seconds').isBefore(moment(address.startTime, 'HH:mm'))
            }
        }

        if (address.timeOfArrival) {
            inTimeSlot = moment(address.timeOfArrival, 'HH:mm').isBetween(moment(address.startTime, 'HH:mm'), moment(address.endTime, 'HH:mm'))

            if (!inTimeSlot) {
                beforeTimeSlot = moment(address.timeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
            }
        }

        // A completed address the messenger can't change anymore is greyed out like an address that can't be started yet
        const isInactive = address.hasArrived && !address.isDepot ? !clickable : !isReady && !address.hasArrived
        const color = showCollo || isInactive ? Colors.textMedium : Colors.textDark

        let formattedDiff = ''
        let tooLate

        if (address.timeOfArrival && address.plannedTimeOfArrival) {
            const diff = moment(address.timeOfArrival, 'HH:mm').diff(moment(address.plannedTimeOfArrival, 'HH:mm'), 'seconds')
            formattedDiff = formatDuration(diff, true)
            tooLate = diff > 60 // minute late
        }

        return (
            <div style={{display: 'flex', width: 'calc(100% - 6px)', flexShrink: 0, marginTop: isSibling ? 0 : 6}}>
                <div style={{
                    height: 36, width: 6, borderRadius: '4px 0px 0px 4px',
                    background: address.isDepot ?
                        `repeating-conic-gradient(#000 0% 25%, #fff 0% 50%) center / ${6}px ${6}px` :
                        address.isRetour ? Colors.retour : Colors[address.type]
                }}
                />

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        background: Colors.backgroundWhite,
                        borderBottom: `solid 1px ${Colors.grey20}`,
                        overflow: 'hidden',
                        paddingTop: 3,
                        paddingBottom: 3
                    }}
                    onClick={clickable ? onClick : () => { }}
                >
                    <Row style={{paddingLeft: 6, paddingRight: 6, alignItems: 'center', minWidth: 'auto'}}>
                        <S1 translate='no' style={{minWidth: 20, color, marginRight: 6, textAlign: 'center'}}>{address.stopNumber || ''}</S1>
                        <div style={{display: 'flex', flex: 1, minWidth: 0, alignItems: 'center'}}>
                            <S1 ellipsis translate='no' style={{minWidth: 0, maxWidth: '100%', color}}>{address.street}&nbsp;</S1>
                            <S1 translate='no' style={{color, marginRight: 6, flexShrink: 0}}>{`${address.nr}${address.addition}${compact && address.city ? `, ${address.city}` : ''}`}</S1>
                        </div>
                        {!address.hasArrived && !address.plannedTimeOfArrival && !address.isDepot &&
                            <P translate='no' style={{color}} ellipsis>{`${address.startTime} - ${address.endTime}`}</P>
                        }

                        {!address.hasArrived && address.plannedTimeOfArrival &&
                            <>
                                <Icon
                                    style={{marginRight: 6, marginLeft: 24, color: inTimeSlot ? color : beforeTimeSlot ? Colors.warningBright : Colors.errorBright}}
                                    icon={inTimeSlot ? 'mdi mdi-clock-outline' : 'mdi mdi-clock-alert-outline'}
                                />

                                <P translate='no' style={{color}}>{moment(address.plannedTimeOfArrival, 'HH:mm').format('HH:mm')}</P>
                            </>
                        }

                        {/* The arrival time of a stop completed by a planner is the calculated ETA, not a timestamp */}
                        {address.hasArrived && !address.completedByReseller &&
                            <>
                                <Icon
                                    style={{marginRight: 6, marginLeft: 24, color: inTimeSlot ? color : beforeTimeSlot ? Colors.warningBright : Colors.errorBright}}
                                    icon={inTimeSlot ? 'mdi mdi-clock-check-outline' : 'mdi mdi-clock-alert-outline'}
                                />

                                <P translate='no' style={{color}}>{address.timeOfArrival}</P>
                            </>
                        }
                    </Row>

                    <Row style={{paddingLeft: 6}}>

                        <S1 style={{minWidth: 20, color, marginRight: 6, textAlign: 'center'}}>

                            {editRoute && !address.hasArrived && address.stopNumber &&
                                <Icon
                                    style={{marginRight: 6}}
                                    icon={address.locked ? 'mdi mdi-lock' : 'mdi mdi-lock-open-variant-outline'}
                                    onClick={async (event) => {
                                        event.stopPropagation()

                                        if (route.ready && address.locked) {
                                            if (!await this.popup.open('Route wijzigen', 'Je ontgrendeld stopnummers van een route die gereed is. Weet je zeker dat je de route wil wijzigen?')) return

                                            await new Promise((resolve) => {
                                                OrderActions.setRouteReady(route._id, false, (err) => {
                                                    if (err) {
                                                        this.popup.setError(err)
                                                    } else {
                                                        resolve()
                                                    }
                                                })
                                            })
                                        }

                                        OrderActions.setAddressLocked(address.orderId, address.index, !address.locked, (err) => {
                                            if (err) {
                                                this.popup.setError(err)
                                            } else {
                                                this.popup.close()
                                            }
                                        })
                                    }}
                                />
                            }
                        </S1>

                        <div style={{flex: 1, minWidth: 0}}>
                            {!compact &&
                                <P ellipsis translate='no' style={{color, marginBottom: 12}}>{`${address.postalCode} ${address.city}`}</P>
                            }

                            {address.name &&
                                <Row style={{alignItems: 'center', marginBottom: compact ? 0 : 12}}>
                                    <Icon style={{marginRight: 6, fontSize: 20, color}} icon={address.isBusiness ? 'mdi mdi-store-outline' : 'mdi mdi-account-outline'} />

                                    <div style={{flex: 1, overflow: 'hidden'}}>
                                        <P ellipsis translate='no' style={{color}}>{address.name}</P>

                                        {!compact && address.attention &&
                                            <P ellipsis translate='no' style={{color}}>{address.attention}</P>
                                        }
                                    </div>
                                </Row>
                            }

                            {!compact &&
                                <Row style={{alignItems: 'center', color, marginBottom: 12}}>
                                    <Icon style={{marginRight: 6, fontSize: 20, color}} icon='mdi mdi-send' />

                                    <P ellipsis style={{color}}>
                                        {address.type === 'pickup' ? 'Opdrachtgever: ' : 'Afzender: '}
                                        <span translate='no'>{`${address.senderAddress?.name || address.customerAddress?.name}`} </span>
                                    </P>
                                </Row>
                            }

                            {!compact && address.originalAddress &&
                                <OriginalAddressRow address={address.originalAddress} style={{marginTop: -6, marginBottom: 12}} />
                            }

                            {address.instructions && !address.hasArrived &&
                                <div style={{display: 'flex', alignItems: 'center', color, marginTop: 6}}>
                                    <Icon style={{marginRight: 6, fontSize: 20, color}} icon='mdi mdi-message-outline' />
                                    <S2 ellipsis translate='no' style={{color, whiteSpace: 'pre-line'}}>{address.instructions}</S2>
                                </div>
                            }

                            <OptionIcons style={{marginLeft: 26}} options={address} colliCount={address.colli?.length} color={color} />
                            <CustomOptionBadges style={{marginLeft: 26, marginTop: 2}} reseller={reseller} customOptions={address.customOptions} />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                width: 'fit-content',
                                paddingLeft: 6,
                                paddingTop: 6

                            }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {address.timeOfArrival && address.plannedTimeOfArrival && formattedDiff && !address.completedByReseller && (
                                <P style={{color: tooLate ? Colors.errorDark : Colors.successDark, paddingRight: 6}}>{formattedDiff}</P>
                            )}

                            {editRoute && !address.hasArrived && address.anchor &&
                                <Icon style={{marginRight: 12, marginTop: 6}} icon='mdi mdi-anchor' />
                            }

                            {editRoute && !address.hasArrived && createdByMessenger && (
                                <IconButton
                                    style={{marginRight: 12}}
                                    onClick={async () => {
                                        if (!await this.popup.open('Verwijder stop', 'Weet je zeker dat je deze stop wilt verwijderen?')) return

                                        try {
                                            await emit('orders.removeMessengerOrder', address.orderId)
                                            this.popup.close()
                                        } catch (err) {
                                            this.popup.setError(err instanceof Error ? err.message : String(err))
                                        }
                                    }}
                                >
                                    <Icon icon='mdi mdi-delete' style={{margin: 0}} />
                                </IconButton>
                            )}

                            {!showCollo && (address.isStarted || address.isDepot) && !address.hasArrived &&
                                <Row style={{flexShrink: 0, justifyContent: 'flex-end', marginTop: 12}}>
                                    {onClickMap &&
                                        <IconButton
                                            style={{marginRight: 12}}
                                            disabled={!address.isReady}
                                            onClick={onClickMap || (() => { })}
                                        >
                                            <Icon style={{marginRight: 6, fontSize: 24}} icon='mdi mdi-map' />
                                        </IconButton>
                                    }

                                    <IconButton
                                        disabled={!address.isReady}
                                        onClick={() => onClickNavigation(address)}
                                    >
                                        <Icon style={{marginRight: 6, fontSize: 24}} icon='mdi mdi-navigation-outline' />
                                    </IconButton>
                                </Row>
                            }

                            <div style={{flex: 1}} />

                            {!showCollo && (!address.isStarted || address.hasArrived) && status && !address.isDepot &&
                                <Row
                                    style={{
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: statusColor,
                                        width: 'fit-content',
                                        padding: '6px 12px 6px 12px',
                                        marginTop: 12
                                    }}
                                >
                                    <P ellipsis onDark={Colors.isDarkBackground(statusColor)}>{status}</P>
                                </Row>
                            }
                        </div>
                    </Row>

                    {showCollo && !address.started && !address.hasArrived && address.colli?.length > 0 && address.colli?.map((collo) => {
                        if ((showCollo === 'open' && collo.ready) || (showCollo === 'ready' && !collo.ready)) {
                            return null
                        }

                        return (
                            <Row
                                key={collo.barcode}
                                style={{alignItems: 'center', height: 36, paddingLeft: 6, paddingRight: 6, minWidth: 'auto'}}
                                onClick={collo.started ?
                                    null :
                                    () => {
                                        ['expected', 'athub'].includes(collo.status) && onSetColloReady(collo)
                                    }}
                            >
                                <S2 style={{fontWeight: 700, marginRight: 12}}>{collo.index + 1}</S2>

                                <P style={{flex: 1}}>{collo.barcode || 'Geen barcode'}</P>

                                {collo.started ?
                                    <P style={{color: Colors.textMedium}}>Onderweg</P> :
                                    !collo.ready && collo.status === 'expected' ?
                                        <Badge>{dbCodes.colloStatus['expected']}</Badge> :
                                        !collo.ready ?
                                            <Badge variant='white'>Gereed</Badge> :
                                            <Badge variant='success'>Geladen</Badge>

                                }
                            </Row>
                        )
                    })}
                </div>

                <Popup ref={(ref) => this.popup = ref} />
            </div>
        )
    }
}
